<template lang="pug">
	.ma-sm-2()

		v-snackbar(
			v-model='snackbar.show'
			:color='snackbar.color'
			:timeout="snackbar.timeout"
			top
		)
			| {{ snackbar.text }}
			v-btn(dark text @click='snackbar.show = false')
				| Close

		template(v-if="displayLoadingCircle")
			.text-center
				v-progress-circular(
					:size='100'
					:width='10'
					color='var(--volie-blue)'
					indeterminate
					style="margin-top: 100px;"
				)

		template(v-else)
			//- Integration/Resource Errors
			template(v-if="isIntegrationError || isIntegrationResourceError")
				v-col(cols='12')
					v-alert(
						outlined
						type="error"
					)
						| An error has occured. Please disposition the appointment creation in Volie and create the appointment in {{ currentIntegration || "the integration provider" | titleize}}.
			
			template(v-if="validIntegrationResponse")
				v-col(cols='12')
					v-alert(
						outlined
						type="error"
					)
						| {{ currentIntegration || "the integration provider" | titleize}} response: {{ integrationResponse }}
			//- CUSTOMERS

			//- No customers found
			v-row.text-center(v-if="showNoCustomersFoundError")
				v-col(cols='12')
					v-alert(
						outlined
						type="warning"
					)
						| Zero {{ currentIntegration || "integration provider" | titleize}} customers found. Please create a new customer.
			v-row.mb-4.mt-2(v-if="showSwitchIntegrationCustomerButton")
				v-col(cols="12" v-if="!switchIntegrationCustomer")
					v-tooltip(top)
						template(v-slot:activator='{ on, attrs }')
							v-btn(
								@click="handleSwitchIntegrationCustomer"
								class="mb-2"
								color="primary"
								outlined
								small
								icon
								v-bind="attrs"
								v-on="on"
							)
								v-icon mdi-account-switch
						span Switch or create new {{ currentIntegration || 'integration' | titleize}} customer		
				v-col(cols="12" v-if="switchIntegrationCustomer")
					v-btn(
						@click="handleCancelSwitchIntegrationCustomer"
						color="grey"
						text
						small
					)
						v-icon(left) mdi-arrow-left
						| Cancel Switch {{ currentIntegration || 'integration' | titleize }} Customer

			//- Create New Customer Form
			template(v-if="showCreateCustomerForm")
				v-col(cols='12')
					//- Back button when coming from customer selection list
					v-row(v-if="validIntegrationCustomers" class="mb-3")
						v-col(cols="12")
							v-btn(
								@click="showCreateCustomerForm = false"
								color="grey"
								text
								small
							)
								v-icon(left) mdi-arrow-left
								| Back to Customer List
					CustomerForm(
						v-model="formCustomer"
						:create-customer="createIntegrationCustomer"
						:current-campaign-customer="currentCampaignCustomer"
						:customer-addresses="customerAddresses"
						:eligible-to-save="eligibleToSaveCustomer"
						:is-customer="true"
						:primary-customer-address="primaryCustomerAddress"
						:show-create="true"
						:show-snackbar="showSnackbar"
					)

			//- Multiple Customers Found - Show Selection List
			template(v-if="displayMatchingCustomers")

				v-container(style="max-width: 800px;")
					//- Matching customers found
					v-row.text-center(v-if="showCustomerSelectionList && !switchIntegrationCustomer")
						v-col(cols='12')
							v-alert(
								outlined
								type="warning"
							)
								div(v-html="'Matching ' + (currentIntegration || 'integration provider') + ' customers found. Please select existing or create a new customer' + (currentIntegration === 'xtime' ? ' on the Xtime website.<br>Xtime does not support creating customers here.' : '.')")

					//- Create New Customer Button
					v-row.mb-4(v-if="currentIntegration !== 'xtime' && !showCreateCustomerForm")
						v-col(cols="12" class="text-center")
							v-btn(
								@click="showCreateCustomerForm = true"
								class="mb-2"
								color="success"
								outlined
								small
							)
								| Create New Customer
								v-icon(color="success") mdi-account-plus

					//- Customer Cards
					v-card(
						v-if="showCustomerSelectionList"
						v-for="(customer, index) in displayedIntegrationCustomers"
						:key="customer.sid || index"
						class="mb-3 position-relative customer-card"
						outlined
						hover
						@click="showCustomerConfirmDialog(customer)"
						style="cursor: pointer; transition: all 0.2s ease;"
					)
						//- hover overlay
						v-overlay(
							:value="true"
							absolute
							class="customer-overlay d-flex align-center justify-center"
							color="rgba(25, 118, 210, 0.85)"
						)
							v-btn(
								color="white"
								outlined
								large
								@click.stop
							)
								v-icon(left) mdi-cursor-pointer
								| Select
						v-row(no-gutters)
							v-col(cols="12" class="pa-1")
								v-row(dense no-gutters)
									v-col(cols="12" class="py-1")
										h4(class="primary--text mb-1")
											StrLimitTooltipText(
												:text="getCustomerFullName(customer)"
												:char-limit="30"
												style="display: inline;"
											)
											span(
												v-if="customer.status"
												:class="customer.status === 'ACTIVE' ? 'success--text' : 'grey--text'"
												class="ml-2 font-weight-medium"
												style="font-size: 0.75rem; display: inline; vertical-align: middle;"
											) ({{ customer.status }})
										v-chip(v-if="customer.company_name" small color="primary" outlined class="ml-2")
											| {{ customer.company_name }}
								v-row(v-if="customer.phones && customer.phones.length > 0" dense no-gutters)
									v-col(cols="12" class="py-1")
										v-icon(small class="mr-2") mdi-phone
										| {{ formatPhoneNumber(customer.phones[0].number) }}
										v-chip(v-if="customer.phones[0].phone_type" x-small color="primary" class="ml-2")
											| {{ customer.phones[0].phone_type }}
										v-chip(v-if="customer.phones[0].is_primary" x-small color="success" class="ml-2")
											| Primary
								v-row(v-if="customer.email" dense no-gutters)
									v-col(cols="12" class="py-1")
										v-icon(small class="mr-2") mdi-email
										StrLimitTooltipText(
											:text="customer.email"
											:char-limit="35"
											style="display: inline;"
										)
								v-row(v-if="customer.addresses && customer.addresses.length > 0" dense no-gutters)
									v-col(cols="12" class="py-1")
										v-icon(small class="mr-2") mdi-map-marker
										StrLimitTooltipText(
											:text="formatAddress(customer.addresses[0])"
											:char-limit="50"
											style="display: inline;"
										)
										v-chip(v-if="customer.addresses[0].address_type" x-small color="primary" class="ml-2")
											| {{ customer.addresses[0].address_type }}
										v-chip(v-if="customer.addresses[0].is_primary || customer.addresses[0].is_current" x-small color="success" class="ml-2")
											| {{ customer.addresses[0].is_primary ? 'Primary' : 'Current' }}
					
				//- Show More Row Component
				ShowMoreRow(
					v-if="showMoreCustomers"
					:handle-show-more="handleShowMoreCustomers"
					:id-key="'integration-customers'"
					:loading="isShowMoreLoading"
					:show-more="showMoreCustomers"
				)

			//- Customer Confirmation Dialog
			v-dialog(
				v-model="showConfirmDialog"
				max-width="600"
				persistent
			)
				v-card
					v-card-title.headline
						v-icon(left color="primary") mdi-account-check
						| Confirm Customer Association
					v-card-text
						| Are you sure you want to associate this customer?
						v-divider.my-3
						//- selected customer preview
						template(v-if="selectedCustomerForConfirm")
							v-row(dense)
								v-col(cols="12")
									h4(class="primary--text mb-1")
										StrLimitTooltipText(
											:text="getCustomerFullName(selectedCustomerForConfirm)"
											:char-limit="30"
											style="display: inline;"
										)
										span(
											v-if="selectedCustomerForConfirm.status"
											:class="selectedCustomerForConfirm.status === 'ACTIVE' ? 'success--text' : 'grey--text'"
											class="ml-2 font-weight-medium"
											style="font-size: 0.75rem; display: inline; vertical-align: middle;"
										) ({{ selectedCustomerForConfirm.status }})
									v-chip(v-if="selectedCustomerForConfirm.company_name" small color="primary" outlined class="ml-2")
										| {{ selectedCustomerForConfirm.company_name }}
							v-row(v-if="selectedCustomerForConfirm.phones && selectedCustomerForConfirm.phones.length > 0" dense)
								v-col(cols="12")
									v-icon(small class="mr-2") mdi-phone
									| {{ formatPhoneNumber(selectedCustomerForConfirm.phones[0].number) }}
									v-chip(
										v-if="selectedCustomerForConfirm.phones[0].phone_type"
										small
										color="primary"
										class="ml-2"
									)
										| {{ selectedCustomerForConfirm.phones[0].phone_type }}
									v-chip(
										v-if="selectedCustomerForConfirm.phones[0].is_primary"
										small
										color="success"
										class="ml-2"
									)
										| Primary
									v-chip(
										v-if="selectedCustomerForConfirm.phones.filter(p => p.number && p.number.trim()).length > 1"
										small
										color="primary"
										outlined
										class="ml-2"
										@click="showExpandedPhones = !showExpandedPhones"
									)
										| +{{ selectedCustomerForConfirm.phones.filter(p => p.number && p.number.trim()).length - 1 }} more
										v-icon(small) {{ showExpandedPhones ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
							v-row(v-if="showExpandedPhones" dense)
								v-col(cols="12" class="pl-8")
									div(v-for="(phone, index) in selectedCustomerForConfirm.phones.slice(1).filter(p => p.number && p.number.trim())" :key="index" class="mb-1")
										| {{ formatPhoneNumber(phone.number) }}
										v-chip(
											v-if="phone.phone_type"
											small
											color="primary"
											class="ml-2"
										)
											| {{ phone.phone_type }}
										v-chip(
											v-if="phone.is_primary"
											small
											color="success"
											class="ml-2"
										)
											| Primary
							v-row(v-if="selectedCustomerForConfirm.email" dense)
								v-col(cols="12")
									v-icon(small class="mr-2") mdi-email
									StrLimitTooltipText(
										:text="selectedCustomerForConfirm.email"
										:char-limit="35"
										style="display: inline;"
									)
							v-row(v-if="selectedCustomerForConfirm.addresses && selectedCustomerForConfirm.addresses.length > 0" dense)
								v-col(cols="12")
									v-icon(small class="mr-2") mdi-map-marker
									StrLimitTooltipText(
										:text="formatAddress(selectedCustomerForConfirm.addresses[0])"
										:char-limit="50"
										style="display: inline;"
									)
									v-chip(
										v-if="selectedCustomerForConfirm.addresses[0].address_type"
										small
										color="primary"
										class="ml-2"
									)
										| {{ selectedCustomerForConfirm.addresses[0].address_type }}
									v-chip(
										v-if="selectedCustomerForConfirm.addresses[0].is_primary || selectedCustomerForConfirm.addresses[0].is_current"
										small
										color="success"
										class="ml-2"
									)
										| {{ selectedCustomerForConfirm.addresses[0].is_primary ? 'Primary' : 'Current' }}
									v-chip(
										v-if="selectedCustomerForConfirm.addresses.filter(a => a && (a.street || a.city || a.state || a.zip)).length > 1"
										small
										color="primary"
										outlined
										class="ml-2"
										@click="showExpandedAddresses = !showExpandedAddresses"
									)
										| +{{ selectedCustomerForConfirm.addresses.filter(a => a && (a.street || a.city || a.state || a.zip)).length - 1 }} more
										v-icon(small) {{ showExpandedAddresses ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
							v-row(v-if="showExpandedAddresses" dense)
								v-col(cols="12" class="pl-8")
									div(v-for="(address, index) in selectedCustomerForConfirm.addresses.slice(1).filter(a => a && (a.street || a.city || a.state || a.zip))" :key="index" class="mb-1")
										| {{ formatAddress(address) }}
										v-chip(
											v-if="address.address_type"
											small
											color="primary"
											class="ml-2"
										)
											| {{ address.address_type }}
										v-chip(
											v-if="address.is_primary || address.is_current"
											small
											color="success"
											class="ml-2"
										)
											| {{ address.is_primary ? 'Primary' : 'Current' }}
					v-card-actions
						v-spacer
						v-btn(
							color="grey"
							text
							@click="cancelCustomerSelection"
						)
							| Cancel
						v-btn(
							color="primary"
							@click="confirmCustomerSelection"
							:disabled="isConfirmingCustomer"
							:loading="isConfirmingCustomer"
						)
							v-icon(v-if="!isConfirmingCustomer" left) mdi-check
							| {{ isConfirmingCustomer ? 'Loading Resources...' : 'Confirm' }}

			//- APPOINTMENTS
			template
				v-container(v-if="!switchIntegrationCustomer")
					v-row.text-center
						v-col(cols='12')
							v-btn.ma-2(
								v-if="showAppointmentCreateButton && !isIntegrationError && !isIntegrationResourceError"
								color="primary"
								outlined
								:disabled="isIntegrationResourceError"
								@click="displaySlotsView()"
							)
								| Create New Appointment

							//- Details State: Show appointments or "no appointments" alert
							template(v-if="isDetailsState")
								Details(
									v-if="validIntegrationAppointments"
									:advisor-map="advisorMap"
									:current-campaign-customer="currentCampaignCustomer"
									:current-integration="currentIntegration"
									:display-details-view="displayDetailsView"
									:display-reschedule-view="displayRescheduleView"
									:get-integration-appointments="getIntegrationAppointments"
									:handle-cancel-integration-appointment="handleCancelIntegrationAppointment"
									:integration-appointments="integrationAppointments"
									:is-loading="isLoading"
									:set-integration-appointment-details="setIntegrationAppointmentDetails"
									:set-integration-error="setIntegrationError"
									:set-scoped-appointment="setScopedAppointment"
									:shop-map="shopMap"
									:show-snackbar="showSnackbar"
									:transportMap="transportMap"
									:titleize="titleize"
								)

								//- No appointments available alert
								v-alert(
									v-if="displayAppointmentAlert"
									outlined
									type='warning'
									dense
									border='left'
								)
									| {{ integrationAppointmentError || "Customer does not have any upcoming appointments" }}

						//- Slots State
						v-col(cols='12' v-if="isSlotsState")
							Slots(
								:current-campaign-customer="currentCampaignCustomer"
								:current-integration="currentIntegration"
								:customer-vehicles="customerVehicles"
								:display-create-view="displayCreateView"
								:display-details-view="displayDetailsView"
								:display-reschedule-view="displayRescheduleView"
								:handle-add-customer-vehicle="handleAddCustomerVehicle"
								:handle-slot-selected="handleSlotSelected"
								:handle-vehicle-change="handleVehicleChange"
								:handle-vehicle-selected="handleVehicleSelected"
								:integration-customer="integrationCustomer"
								:integration-options="integrationOptions"
								:integration-service-advisors="integrationServiceAdvisors"
								:integration-shops="integrationServiceShops"
								:integration-transportation-types="integrationTransportationTypes"
								:is-change-params-for-integration-resource-error-allowed="!integrationsAllowingChangeParamsForIntegrationResourceError.includes(currentIntegration)"
								:is-integration-service-advisors-loading="isIntegrationServiceAdvisorsLoading"
								:is-integration-service-shops-loading="isIntegrationServiceShopsLoading"
								:is-integration-transportation-types-loading="isIntegrationTransportationTypesLoading"
								:is-service-menu-loading="isServiceMenuLoading"
								:opcode-to-services="opcodeToServices"
								:scoped-integration-appointment="scopedAppointment"
								:selected-vehicle="selectedVehicle"
								:service-to-opcodes="serviceToOpcodes"
								:set-integration-error="setIntegrationError"
								:set-selected-vehicle="handleSetSelectedVehicle"
								:show-snackbar="showSnackbar"
								:vehicle-info="formCustomer?.vehicleInfo"
								:first-customer-vehicle="firstCustomerVehicle"
							)

						//- Create State
						v-col(cols='12' v-if="isCreateState && validIntegrationCustomer")
							Create(
								:add-new-appointment="addNewAppointment"
								:current-campaign-customer="currentCampaignCustomer"
								:current-integration="currentIntegration"
								:customer-addresses="customerAddresses"
								:customer-vehicles="customerVehicles"
								:first-customer-vehicle="firstCustomerVehicle"
								:display-details-view="displayDetailsView"
								:display-slots-view="displaySlotsView"
								:eligible-to-save-delivery-contact="eligibleToSaveCustomer"
								:get-integration-appointments="getIntegrationAppointments"
								:handle-add-customer-vehicle="handleAddCustomerVehicle"
								:integration-customer="integrationCustomer"
								:integration-vehicles="integrationVehicles"
								:primary-customer-address="primaryCustomerAddress"
								:scoped-slot="scopedSlot"
								:selected-vehicle="selectedVehicle"
								:set-appointment-id-for-integration="setAppointmentIdForIntegration"
								:set-integration-appointment-details="setIntegrationAppointmentDetails"
								:set-integration-error="setIntegrationError"
								:set-selected-vehicle="handleSetSelectedVehicle"
								:show-snackbar="showSnackbar"
								:slot-form-object="slotFormObject"
								:update-integration-vehicles="updateIntegrationVehicles"
								:vehicle-info="formCustomer?.vehicleInfo"
								:customer-communication-info="customerCommunicationInfo"
							)

						//- Reschedule State
						v-col(cols='12' v-if="isRescheduleState")
							Reschedule(
								:current-campaign-customer="currentCampaignCustomer"
								:current-integration="currentIntegration"
								:customer-vehicles="customerVehicles"
								:display-details-view="displayDetailsView"
								:display-slots-view="displaySlotsView"
								:get-customer-vehicles="getCustomerVehicles"
								:get-integration-appointments="getIntegrationAppointments"
								:handle-update-integration-appointments="handleUpdateIntegrationAppointments"
								:scoped-appointment="scopedAppointment"
								:scoped-slot="scopedSlot"
								:set-appointment-id-for-integration="setAppointmentIdForIntegration"
								:set-integration-appointment-details="setIntegrationAppointmentDetails"
								:set-integration-error="setIntegrationError"
								:show-snackbar="showSnackbar"
								:slot-form-object="slotFormObject"
							)


</template>

<script lang="ts">
import Vue from 'vue';

// Components
import Create from '@/components/communication_center/shared/tabs/integration/create.vue';
import Details from '@/components/communication_center/shared/tabs/integration/details.vue';
import Slots from '@/components/communication_center/shared/tabs/integration/slots.vue';
import Reschedule from '@/components/communication_center/shared/tabs/integration/reschedule.vue';
import CustomerForm from '@/components/communication_center/shared/tabs/integration/customer_form.vue';
import StrLimitTooltipText from '@/components/shared/str_limit_tooltip_text.vue';
import ShowMoreRow from '@/components/shared/show_more_row_new.vue';

import ExpandableCardListItem from '@/components/shared/expandable_card_list_item.vue';
import ListItem from '@/components/shared/list_item.vue';

// Data
import { resourceColorClass, } from "@/helpers/css_classes";

// helpers
import { formatPhoneNumber } from "@/helpers/phone_number";
import { titleize } from "@/helpers/general";

import { CreateIntegrationCustomer, CreateIntegrationCampaignCustomerAssociation, GetCustomerVehicles, GetIntegrationAppointments, GetIntegrationCustomers, GetIntegrationServiceAdvisors, GetIntegrationServiceMenu, GetIntegrationServiceShops, GetIntegrationTransportationTypes } from "@/data";

export default Vue.extend({
	data() {
		return {
			advisorMap: new Map(),
			appointmentSlots: [],
			switchIntegrationCustomer: false,
			currentView: 'details',
			customerVehicles: [],
			findSlots: false,
			integrationAppointmentError: null,
			integrationResponse: null,
			integrationServiceAdvisors: [],
			integrationServiceShops: [],
			integrationTransportationTypes: [],
			isConfirmingCustomer: false,
			isIntegrationAppointmentsLoading: false,
			isIntegrationCustomerLoading: false,
			isIntegrationError: false,
			isIntegrationResourceError: false,
			isLoading: false,
			opcodeToServices: {},
			organizationId: this.currentCampaignCustomer.organization_id,
			scopedAppointment: null,
			scopedSlot: null,
			selectedCustomerForConfirm: null,
			selectedVehicle: null,
			serviceToOpcodes: {},
			shopMap: new Map(),
			showConfirmDialog: false,
			showCreateCustomerForm: false,
			showExpandedAddresses: false,
			showExpandedPhones: false,
			slotFormObject: null,
			transportMap: new Map(),
			integrationAppointments: [],
			isServiceMenuLoading: false,
			isIntegrationServiceShopsLoading: false,
			isIntegrationTransportationTypesLoading: false,
			isIntegrationServiceAdvisorsLoading: false,
			allowedIntegrationsForGetServiceShops: ["tekion"],
			allowedIntegrationsForGetTransportationTypes: ["tekion", "xtime"],
			allowedIntegrationsForGetServiceAdvisors: ["tekion", "xtime"],
			allowedIntegrationsForGetServiceMenu: ["xtime"], // TODO: add tekion
			integrationsUsingVehicleParamsForResourceRequests: ["xtime"],
			selectedXtimeCustomer: null,
			selectedXtimeVehicle: null,
			selectedTekionCustomer: null,
			selectedTekionVehicle: null,
			integrationCustomer: null,
			integrationVehicles: [],
			integrationAppointmentDetails: null,
			integrationCustomerList: [],
			isChangeParamsForIntegrationResourceErrorAllowed: false,
			displayedIntegrationCustomers: [],
			isExactMatch: false,
			nextFetchKey: "",
			leftOverCustomers: [],
			
			// Pagination for customers
			customerLimit: 3,         // How many customers to show per "page"
			isShowMoreLoading: false, // Loading state for "Show More" button
			showMoreCustomers: false, // Whether to show the "Show More" button
			integrationsAllowingChangeParamsForIntegrationResourceError: ["xtime"],
			allowedParamsForGetIntegrationCustomers: {
				tekion: new Set(["customer_phone", "customer_email", "customer_company_name", "customer_first_name", "customer_last_name"]),
				xtime: new Set(["customer_phone"]),
			},
			formCustomer: {
				companyName: null,
				customerType: null,
				email: null,
				firstName: null,
				lastName: null,
				phones: [{
					phoneNumber: null,
					phoneType: null,
					isPrimary: null,
				}],
				vehicleInfo: {
					customerVehicleId: null,
					make: null,
					model: null,
					sid: null,
					vehicleId: null,
					vin: null,
					year: null,
				},
				customerVehicleId: null,
			}, 
			integrationOptions: {
				integration: this.currentIntegration || "",
				organization_id: this.currentCampaignCustomer.organization_id || 0,
				vehicle_year: "",
				vehicle_make: "",
				vehicle_model: "",
			},
			snackbar: {
				show: false,
				timeout: 2500,
			},
		}
	},
	methods: {
		addNewAppointment(appointment) {

			if (!appointment || !this.integrationAppointments) {
				return;
			}
			this.integrationAppointments.push(appointment);
			this.scopedSlot = null;
		},
		associatedTekionCustomer(integrationCustomers) {
			if (integrationCustomers.length > 0 && this.currentCampaignCustomer?.tekion_customer_sid?.length > 0) {
				return integrationCustomers.find(tekionCustomer => tekionCustomer.sid === this.currentCampaignCustomer?.tekion_customer_sid);
			}
			return null;
		},
		cancelCustomerSelection() {
			this.showConfirmDialog = false;
			this.selectedCustomerForConfirm = null;
		},
		handleSetSelectedVehicle(vehicle) {
			this.setSelectedVehicle(vehicle);
			this.resetCalendar();
		},
		setIntegrationSpecificCustomer(customer) {
			if (this.currentIntegration === "xtime") {
				this.selectedXtimeCustomer = { ...customer };
			}
			if (this.currentIntegration === "tekion") {
				this.selectedTekionCustomer = { ...customer };
			}

			return;
		},
		async confirmCustomerSelection() {
			if (this.selectedCustomerForConfirm) {
				try {
					// Start loading state
					this.isConfirmingCustomer = true;

					// set integration customer
					this.integrationCustomer = { ...this.selectedCustomerForConfirm };

					// set selected customer for xtime and tekion
					this.setIntegrationSpecificCustomer(this.selectedCustomerForConfirm);
					this.createIntegrationCampaignCustomerAssociation();

					// set integration customer vehicles
					if (this.selectedCustomerForConfirm?.vehicle_info?.length > 0) {
						this.integrationVehicles = [...this.selectedCustomerForConfirm.vehicle_info];
					}

					// set vehicle options
					this.resetIntegrationResourceRequestVehicleOptions();

					// get integration appointments
					await this.getIntegrationAppointments();
					await this.sendIntegrationResourceRequests();
					
				} catch (error) {
					this.showSnackbar('error', 'Failed to load customer resources. Please try again.');
				} finally {
					// End loading state
					this.isConfirmingCustomer = false;
					this.switchIntegrationCustomer = false;
					this.resetDisplayedIntegrationCustomers();
				}
			}
			this.showConfirmDialog = false;
			this.selectedCustomerForConfirm = null;
		},
		async createIntegrationCustomer() {
			// Validation
			if (!this.currentIntegration || !this.currentCampaignCustomer || !this.formCustomer) {
				this.showSnackbar("error", `Unable to create ${this.titleize(this.currentIntegration || "integration provider")} customer`);
				return;
			}

			// if the integration create customer endpoint does not exist, set customer from the form elements and return out
			if (!this.currentIntegrationHasCreateCustomerEndpoint) {
				this.isIntegrationCustomerLoading = true;

				try {
					this.setIntegrationCustomerFromFormCustomer();
					await this.sendIntegrationResourceRequests();
					// does not call getIntegrationAppointments because it is a newly created customer

					this.showCreateCustomerForm = false;
					this.displayDetailsView();
				} catch (error) {
					this.isIntegrationResourceError = true;
					this.showSnackbar("error", `Unable to set ${this.titleize(this.currentIntegration || "integration provider")} customer details`);
				} finally {
					this.isIntegrationCustomerLoading = false;
				}

				return;
			}

			const options = {
				integration: this.currentIntegration,
				organization_id: this.currentCampaignCustomer?.organization_id || 0,
				customer_first_name: this.formCustomer?.firstName || "",
				customer_last_name: this.formCustomer?.lastName || "",
				customer_email: this.formCustomer?.email || "",
				customer_phone_number: this.formCustomer?.phones?.[0]?.phoneNumber || "",
				customer_phone_type: this.formCustomer?.phones?.[0]?.phoneType || "",
				customer_phone_is_primary: this.formCustomer?.phones?.[0]?.isPrimary || false,
				customer_type: this.formCustomer?.customerType || "",
				customer_company_name: this.formCustomer?.companyName || "",
				customer_address_line_1: this.formCustomer?.address?.addressLine1 || "",
				customer_address_line_2: this.formCustomer?.address?.addressLine2 || "",
				customer_address_city: this.formCustomer?.address?.city || "",
				customer_address_state: this.formCustomer?.address?.state || "",
				customer_address_zip: this.formCustomer?.address?.zipCode || "",
				customer_address_country: this.formCustomer?.address?.country || "",
				customer_address_is_primary: this.formCustomer?.address?.isPrimary || true,
				customer_address_type: this.formCustomer?.address?.addressType || "SHIPPING",
			};

			this.isIntegrationCustomerLoading = true;

			CreateIntegrationCustomer(options).then(async (response) => {
				if (response?.Data?.integration_customer) {
					this.integrationCustomer = { ...response.Data.integration_customer };
					
					// set selected customer and create integration campaign customer association
					this.selectedCustomerForConfirm = { ...response.Data.integration_customer };
					this.setIntegrationSpecificCustomer(this.selectedCustomerForConfirm);
					this.createIntegrationCampaignCustomerAssociation()

					// Set integration customer vehicles
					if (response.Data.integration_customer.vehicle_info?.length > 0) {
						this.integrationVehicles = [...response.Data.integration_customer.vehicle_info];
					}

					// set integration appointments error to null
					this.integrationAppointmentError = null;
					await this.sendIntegrationResourceRequests();
					this.showSnackbar("success", `Successfully created ${this.titleize(this.currentIntegration) || "integration provider"} customer`);
				} else {
					this.showSnackbar("error", `Unable to create ${this.titleize(this.currentIntegration) || "integration provider"} customer`);

					if (response?.Data?.api_status > 0) {
						this.setIntegrationError(response.Data.api_status);
					}
				}

				this.isIntegrationCustomerLoading = false;
				this.showCreateCustomerForm = false; // Reset form state after successful creation
				this.switchIntegrationCustomer = false;
				this.displayDetailsView();
				this.resetDisplayedIntegrationCustomers();
			});
		},
		displayCreateView() {
			this.currentView = 'create'
		},
		displayDetailsView() {
			this.currentView = 'details';
			this.scopedSlot = null;
			this.scopedAppointment = null;
		},
		displayRescheduleView(slot) {
			this.currentView = 'reschedule'
			this.setScopedSlot(slot);
		},
		displaySlotsView() {
			this.currentView = 'slots-selector'
			this.scopedSlot = null;
		},
		formatAddress(address) {
			if (!address) return '';
			
			const parts = [];
			if (address.line1) parts.push(address.line1);
			if (address.line2) parts.push(address.line2);
			if (address.city) parts.push(address.city);
			if (address.state) parts.push(address.state);
			// Try different possible zip code field names
			if (address.zip_code) parts.push(address.zip_code);
			else if (address.zipcode) parts.push(address.zipcode);
			else if (address.zip) parts.push(address.zip);
			else if (address.postal_code) parts.push(address.postal_code);
			if (address.country) parts.push(address.country);
			
			return parts.join(', ');
		},
		formatPhoneNumber(phoneNumber) {
			return formatPhoneNumber(phoneNumber);
		},
		getAdvisorDisplayName(advisor) {
			if (!advisor) {
				return "";
			}

			let displayName = "";

			if (advisor.display_name?.length > 0) {
				displayName = advisor.display_name;
			} else if (advisor.name?.length > 0) {
				displayName = advisor.name;
			} else {
				const firstName = advisor.first_name || "";
				const lastName = advisor.last_name || "";
				displayName = `${firstName} ${lastName}`.trim();
			}

			// Titleize the display name for consistent formatting
			return displayName ? this.titleize(displayName.toLowerCase()) : "";
		},
		getCustomerFullName(customer) {
			if (!customer) return '';
			
			// Titleize each name component individually
			const firstName = customer.first_name ? this.$options.filters.titleize(customer.first_name) : '';
			const lastName = customer.last_name ? this.$options.filters.titleize(customer.last_name) : '';
			
			return `${firstName} ${lastName}`.trim();
		},
		getCustomerVehicles() {
			const options = {
				account_id: this.currentCampaignCustomer?.account_id || 0,
				customer_id: this.currentCampaignCustomer?.customer_id || 0,
				organization_id: this.currentCampaignCustomer?.organization_id || 0,
			};

			this.isCustomerVehiclesLoading = true;

			return GetCustomerVehicles(options).then((response) => {

				if (response?.Data?.customer_vehicles?.length > 0) {
					// remove vehicles with invalid fields
					const newVehicles = this.filterValidCustomerVehicles(response.Data.customer_vehicles);

					// Set on array
					if (this.customerVehicles?.length > 0) {
						this.customerVehicles = this.customerVehicles.concat(newVehicles);
					} else {
						this.customerVehicles = newVehicles;
					}
				}

				this.isCustomerVehiclesLoading = false;
			});
		},
		async getIntegrationAppointments() {
			this.isIntegrationAppointmentsLoading = true;
			this.integrationAppointmentError = null;

			try {
				if (!this.integrationCustomer) {
					this.integrationAppointmentError = `An error occurred with the ${this.titleize(this.currentIntegration || "integration provider")} appointment request.`;
					return;
				}

				this.integrationAppointments = [];
				this.integrationAppointmentError = null;

				let phoneNumber = ""
				if (this.currentIntegration === "xtime") {
					// loop and get first primary phone
					if (this.integrationCustomer?.phones?.length > 1) {
						for (let i = 0; i < this.integrationCustomer?.phones?.length; i++) {
							if (this.integrationCustomer?.phones?.[i]?.is_primary) {
								phoneNumber = this.integrationCustomer?.phones?.[i]?.number || "";
								break;
							}
						}
					}

					// else set first phone
					if (phoneNumber.length === 0) {
						phoneNumber = this.integrationCustomer?.phones?.[0]?.number || "";
					}
				}

				const options = {
					integration: this.currentIntegration || "",
					organization_id: this.currentCampaignCustomer.organization_id,
					customer_sid: this.integrationCustomer?.sid || "",
					phone_number: phoneNumber,
				};

				const response = await GetIntegrationAppointments(options);

				if (response?.Data?.integration_appointments?.length > 0) {
					this.integrationAppointments = response.Data.integration_appointments;
				} else if (response?.Error?.length > 0) {
					this.integrationAppointmentError = `An error occurred retrieving the ${this.titleize(this.currentIntegration) || "integration provider"} appointment data`;
				} else if (response?.Data?.api_status === 200) {
					this.integrationAppointmentError = null;
				} else {
					this.integrationAppointmentError = `Failed to load ${this.titleize(this.currentIntegration) || "integration provider"} appointments`;
				}
			} finally {
				this.isIntegrationAppointmentsLoading = false;
			}
		},
		async getIntegrationCustomers() {
			if (!(this.currentCampaignCustomer?.customer)) {
				return;
			}

			// Set loading states
			if (this.integrationCustomerList?.length > 0) {
				this.isShowMoreLoading = true;
			} else {
				this.isIntegrationCustomerLoading = true;
			}

			// reset integration error
			this.integrationAppointmentError = null;

			try {

				const options = {
					integration: this.currentIntegration || "",
					is_exact_match: this.isExactMatch,
					next_fetch_key: this.nextFetchKey || "",
					organization_id: this.currentCampaignCustomer.organization_id || 0,

					// params variable by integration
					customer_company_name: this.isGetIntegrationCustomersParamAllowed("customer_company_name") ? this.customerCommunicationInfo?.companyName || "" : "",
					customer_email: this.isGetIntegrationCustomersParamAllowed("customer_email") ? this.customerCommunicationInfo?.customerEmailAddresses?.[0]?.emailAddress || "" : "",
					customer_first_name: this.isGetIntegrationCustomersParamAllowed("customer_first_name") ? this.customerCommunicationInfo?.customerFirstName || "" : "",
					customer_last_name: this.isGetIntegrationCustomersParamAllowed("customer_last_name") ? this.customerCommunicationInfo?.customerLastName || "" : "",
					customer_phone: this.isGetIntegrationCustomersParamAllowed("customer_phone") ? this.customerCommunicationInfo?.customerPhoneNumbers?.[0]?.phoneNumber?.replace(/[()-]/g, '') || "" : "",
				};

				const response = await GetIntegrationCustomers(options);

				if (response?.Data?.integration_customers?.length > 0) {
					// set next fetch key and exact match
					this.nextFetchKey = response?.Data?.next_fetch_key
					this.isExactMatch = response?.Data?.exact_customer_match

					// set integration customer only if exactly matched to parameters and only one customer returned, otherwise show options
					let isEligibleToSetIntegrationCustomer = false;
					let phonesMatch = false;
					let emailsMatch = false;
					let matchedTekionCustomer = null;

					let associatedCustomer = this.associatedTekionCustomer(response?.Data?.integration_customers);
					let returnedCustomerList = response?.Data?.integration_customers;

					if (response?.Data?.exact_customer_match && returnedCustomerList?.length === 1) {
						let exactMatchedCustomer = returnedCustomerList?.[0];
						if (exactMatchedCustomer?.phones?.length > 0 && options.customer_phone?.length > 0) {
							for (let i = 0; i < exactMatchedCustomer?.phones?.length; i++) {
								// if the last 10 digits of the phone number match the last 10 digits of the params phone number (tekion edge case)
								if (exactMatchedCustomer?.phones?.[i]?.number?.length > 10 && options.customer_phone?.length > 10) {
									if (exactMatchedCustomer?.phones?.[i]?.number?.slice(-10) === options.customer_phone?.slice(-10)) {
										phonesMatch = true;
										break;
									}
								} else {
									if (exactMatchedCustomer?.phones?.[i]?.number === options.customer_phone) {
										phonesMatch = true;
										break;
									}
								}
							}
						} else {
							phonesMatch = true;
						}

						if (exactMatchedCustomer?.email?.length > 0 && options.customer_email?.length > 0) {
							if (exactMatchedCustomer?.email === options.customer_email) {
								emailsMatch = true;
							}
						}

						if (phonesMatch || emailsMatch) {
							matchedTekionCustomer = exactMatchedCustomer;
							isEligibleToSetIntegrationCustomer = (
								(matchedTekionCustomer?.company_name || "") === (options.customer_company_name || "") &&
								(matchedTekionCustomer?.first_name || "") === (options.customer_first_name || "") &&
								(matchedTekionCustomer?.last_name || "") === (options.customer_last_name || "")
							)
						}
					} else if (associatedCustomer && this.currentIntegration === "tekion") {
						isEligibleToSetIntegrationCustomer = true;
						matchedTekionCustomer = associatedCustomer;
						returnedCustomerList = returnedCustomerList.filter(customer => customer.sid !== associatedCustomer?.sid);
					}

					// if exact match and integration customers returned is only one
					// OR if a tekion customer is already associated with the campaign customer
					if (isEligibleToSetIntegrationCustomer) {
						this.integrationCustomer = { ...matchedTekionCustomer };

						// set integration specific customer
						this.setIntegrationSpecificCustomer(this.integrationCustomer);

						// set integration customer vehicles
						if (matchedTekionCustomer?.vehicle_info?.length > 0) {
							this.integrationVehicles = [...matchedTekionCustomer?.vehicle_info];
						}

						// send integration resource requests and get integration appointments
						await this.sendIntegrationResourceRequests();
						await this.getIntegrationAppointments();
					}

						// handle leftover customers from previous pagination
						let totalUndisplayedCustomers = [];
						if (this.leftOverCustomers?.length > 0) {
							totalUndisplayedCustomers.push(...this.leftOverCustomers);
							totalUndisplayedCustomers.push(...response?.Data?.integration_customers);
						}

					// calculate number of new customers to display
					let numberOfNewCustomersToDisplay = this.customerLimit - (this.leftOverCustomers?.length || 0);

						if (this.integrationCustomerList?.length > 0) {
							this.integrationCustomerList = this.integrationCustomerList.concat(response?.Data?.integration_customers);
							this.displayedIntegrationCustomers = this.displayedIntegrationCustomers.concat(totalUndisplayedCustomers.slice(0, numberOfNewCustomersToDisplay));
							this.leftOverCustomers = null;
						} else {
							this.integrationCustomerList = response?.Data?.integration_customers;
							totalUndisplayedCustomers.push(...response?.Data?.integration_customers);
							this.displayedIntegrationCustomers = totalUndisplayedCustomers.slice(0, numberOfNewCustomersToDisplay);
							this.leftOverCustomers = null;
						}

					// show more logic
					if (this.displayedIntegrationCustomers.length < this.integrationCustomerList.length) {
						this.showMoreCustomers = true;
					} else if (response?.Data?.total_customers === 0 || (response?.Data?.total_customers === this.integrationCustomerList?.length)) {
						this.showMoreCustomers = false;
					}

					if (associatedCustomer && associatedCustomer?.sid) {
						this.displayedIntegrationCustomers = this.integrationCustomerList.filter(customer => customer.sid !== associatedCustomer?.sid);
					}

				} else if (response?.Error || response?.Data?.api_status >= 400) {
					// error or api error
					this.showMoreCustomers = false;
					this.isIntegrationError = true;
					this.showCreateCustomerForm = false;
					if (response?.Data?.message?.length > 0) {
						this.integrationResponse = response.Data.message;
					}

					this.showSnackbar('error', `An error occurred with the ${this.titleize(this.currentIntegration || "integration provider")} customer request.`);
				} else {
					// no customers found
					this.showCreateCustomerForm = true;
					this.showMoreCustomers = false;
				}
			} finally {
				// Set loading to false after all operations are complete so that customer form can be displayed correctly
				this.isIntegrationCustomerLoading = false;
				this.isShowMoreLoading = false;
			}
		},
		getServiceAdvisors() {
			this.isServiceAdvisorsLoading = true;

			return GetIntegrationServiceAdvisors(this.integrationOptions).then((response) => {
				if (response?.Data?.integration_service_advisors) {
					this.integrationServiceAdvisors = response?.Data?.integration_service_advisors || [];

					// Normalize advisor names and build advisor map
					this.integrationServiceAdvisors.forEach(advisor => {
						if (advisor?.sid?.length === 0) {
							return; // skip to next
						}
						const advisorName = this.getAdvisorDisplayName(advisor);
						// Update the original name/display_name for UI display
						if (advisor.display_name?.length > 0) {
							advisor.display_name = advisorName;
						} else if (advisor.name?.length > 0) {
							advisor.name = advisorName;
						} else {
							// Set display_name if neither exists
							advisor.display_name = advisorName;
						}
						this.advisorMap?.set(advisor.sid, advisorName);
					});
				} else {
					// give the ability to change customer vehicle params
					if (!this.integrationsAllowingChangeParamsForIntegrationResourceError.includes(this.currentIntegration)) {
						this.isIntegrationResourceError = true;
						this.showSnackbar('error', 'Failed to load service advisors');
					}
				}

				this.isServiceAdvisorsLoading = false;
			});
		},
		getServiceMenu() {
			this.isServiceMenuLoading = true;

			// return for the promise to be handled by the caller
			return GetIntegrationServiceMenu(this.integrationOptions).then((response) => {
				if (response?.Data?.opcode_to_services) {
					this.opcodeToServices = response.Data.opcode_to_services;
					this.serviceToOpcodes = response.Data.service_to_opcodes;
				} else {
					// give the ability to change customer vehicle params
					if (!this.integrationsAllowingChangeParamsForIntegrationResourceError.includes(this.currentIntegration)) {
						this.isIntegrationResourceError = true;
						this.showSnackbar('error', 'Failed to load service menu');
					}
				}

				this.isServiceMenuLoading = false;
			});
		},
		getShops() {
			this.isServiceShopsLoading = true;

			// return for the promise to be handled by the caller
			return GetIntegrationServiceShops(this.integrationOptions).then((response) => {
				if (response?.Data?.integration_service_shops) {
					this.integrationServiceShops = response.Data.integration_service_shops;

					// Normalize shop names and build shop map
					this.integrationServiceShops.forEach(shop => {
						if (shop?.sid?.length === 0) {
							return; // skip to next
						}
						const shopName = this.titleize(shop.name?.toLowerCase()) || "";
						shop.name = shopName; // Update the original name for UI display
						this.shopMap?.set(shop.sid, shopName);
					});
				} else {
					// give the ability to change customer vehicle params
					if (!this.integrationsAllowingChangeParamsForIntegrationResourceError.includes(this.currentIntegration)) {
						this.isIntegrationResourceError = true;
						this.showSnackbar('error', 'Failed to load shops');
					}
				}

				this.isServiceShopsLoading = false;
			});
		},
		getTransportationTypes() {
			this.isTransportationTypesLoading = true;

			return GetIntegrationTransportationTypes(this.integrationOptions).then((response) => {
				if (response?.Data?.integration_transportation_types) {
					this.integrationTransportationTypes = response?.Data?.integration_transportation_types || [];

					// Normalize transportation type names and build transport map
					this.integrationTransportationTypes.forEach(transport => {
						if (transport?.sid?.length === 0) {
							return; // skip to next
						}
						const transportName = this.titleize(transport.name?.toLowerCase()) || "";
						transport.name = transportName; // Update the original name for UI display
						this.transportMap?.set(transport.sid, transportName);
					});
				} else {
					// give the ability to change customer vehicle params
					if (!this.integrationsAllowingChangeParamsForIntegrationResourceError.includes(this.currentIntegration)) {
						this.isIntegrationResourceError = true;
						this.showSnackbar('error', 'Failed to load transportation types');
					}
				}

				this.isTransportationTypesLoading = false;
			});
		},
		handleAddCustomerVehicle(newVehicle) {
			if (!newVehicle) {
				return;
			}

			this.customerVehicles.push(newVehicle);
		},
		handleCancelIntegrationAppointment(canceledAppointment) {
			const index = this.integrationAppointments.findIndex(
				appointment => appointment.appointment_sid === canceledAppointment.appointment_sid
			);

			if (index !== -1) {
				this.integrationAppointments.splice(index, 1);
			}
		},
		handleCancelSwitchIntegrationCustomer() {
			this.switchIntegrationCustomer = false;
			this.showCreateCustomerForm = false;
		},
		handleSwitchIntegrationCustomer() {
			if (this.integrationCustomerList?.length > 0) {
				this.displayedIntegrationCustomers = this.integrationCustomerList.slice(0, this.customerLimit);
			} else {
				this.getIntegrationCustomers();
				if (this.integrationCustomerList?.length == 0) {
					this.showCreateCustomerForm = true;
				}
			}

			this.switchIntegrationCustomer = true;
		},
		handleShowMoreCustomers() {
			if (this.displayedIntegrationCustomers?.length === 0 || this.integrationCustomerList?.length === 0) {
				return;
			}

			const newLength = this.displayedIntegrationCustomers.length + this.customerLimit;
			const newSlice = this.integrationCustomerList.slice(this.displayedIntegrationCustomers.length, newLength)
			if (newSlice.length > 0) {
				this.showMoreCustomers = true;

				// set leftover customers
				if (newSlice.length < this.customerLimit) {
					this.leftOverCustomers = newSlice;

					// if next fetch key is available, fetch more
					if (this.nextFetchKey?.length > 0) {
						this.getIntegrationCustomers();
					} else {
						this.showMoreCustomers = false;
						this.leftOverCustomers = [];
					}
				}

				this.displayedIntegrationCustomers = this.displayedIntegrationCustomers.concat(newSlice);

				return;
			}

			// if all displayed, fetch more from server
			if (this.displayedIntegrationCustomers.length >= this.integrationCustomerList.length) {
				this.getIntegrationCustomers();
			}
		},
		handleSlotSelected(slot, formObject, isEdit) {
			this.setScopedSlot(slot);
			this.setSlotFormObject(formObject);

			if (isEdit) {
				this.displayRescheduleView(slot);
			} else {
				this.displayCreateView();
			}
		},
		handleVehicleChange(customerVehicleId) {
			if (!customerVehicleId || this.selectedVehicle?.customer_vehicle_id === customerVehicleId) {
				return;
			}
			
			this.handleVehicleSelected(customerVehicleId);
			this.resetCalendar();
		},
		handleVehicleSelected(customerVehicleId) {
			this.selectedVehicle = this.customerVehicles.find(vehicle => vehicle.customer_vehicle_id === customerVehicleId) || null;
		},
		initFormCustomer() {
			if (!(this.customerCommunicationInfo)) {
				return;
			}

			let customerType = "INDIVIDUAL";
			if (this.customerCommunicationInfo?.companyName?.length > 0) {
				customerType = "BUSINESS";
			}

			let customerPhones = [];
			if (this.customerCommunicationInfo?.customerPhoneNumbers?.length > 0) {
				switch (this.customerCommunicationInfo.customerPhoneNumbers[0]?.phoneType?.toLowerCase()) {
					case "home":
						this.customerCommunicationInfo.customerPhoneNumbers[0].phoneType = "HOME";
						break;
					case "work":
						this.customerCommunicationInfo.customerPhoneNumbers[0].phoneType = "WORK";
						break;
					case "cell":
						this.customerCommunicationInfo.customerPhoneNumbers[0].phoneType = "MOBILE";
						break;
				}

				customerPhones.push(this.customerCommunicationInfo.customerPhoneNumbers[0]);
			}

			this.formCustomer = {
				companyName: this.customerCommunicationInfo?.companyName || null,
				customerType: customerType,
				email: this.customerCommunicationInfo?.customerEmailAddresses?.[0]?.emailAddress || null,
				firstName: this.customerCommunicationInfo?.customerFirstName || null,
				lastName: this.customerCommunicationInfo?.customerLastName || null,
				phones: customerPhones || [],
				address: this.primaryCustomerAddress || this.customerAddresses?.[0] || null,
				customerVehicleId: null,
			};
		},
		isGetIntegrationCustomersParamAllowed(param) {
			return this.allowedParamsForGetIntegrationCustomers[this.currentIntegration]?.has(param);
		},
		async createIntegrationCampaignCustomerAssociation() {
			if (!this.selectedCustomerForConfirm) {
				return;
			}
			const integrationCustomerSid = this.selectedCustomerForConfirm.sid;

			const params = {
				campaign_customer_id: this.currentCampaignCustomer.campaign_customer_id,
				integration: this.currentIntegration,
				integration_customer_sid: integrationCustomerSid,
			}

			CreateIntegrationCampaignCustomerAssociation(params).then((response) => {
				if (response?.Data?.ok) {
					this.setIntegrationCustomerSid(integrationCustomerSid);
				}
			});
		},
		reInitIntegrationValues() {
			this.isLoading = true;
			this.resetAllIntegrationValues();
			this.getCustomerVehicles();

			if (this.hasPreviouslySetIntegrationCustomer) {
				this.restoreLastIntegrationValues();
			} else {
				this.initFormCustomer();
				this.getIntegrationCustomers();
			}
			this.isLoading = false;
		},
		resetCalendar() {
			if (this.currentIntegration === "xtime") {
				this.resetIntegrationResourceRequestOptions();
				this.resetIntegrationCalendarResources();
				this.sendIntegrationResourceRequests();
			}

			return;
		},
		resetIntegrationResourceRequestVehicleOptions() {
			this.integrationOptions.vehicle_year = this.integrationsUsingVehicleParamsForResourceRequests.includes(this.currentIntegration) ? this.selectedVehicle?.vehicle_model_year || this.firstCustomerVehicle?.vehicle_model_year || "" : "";
			this.integrationOptions.vehicle_make = this.integrationsUsingVehicleParamsForResourceRequests.includes(this.currentIntegration) ? this.selectedVehicle?.vehicle_make || this.firstCustomerVehicle?.vehicle_make || "" : "";
			this.integrationOptions.vehicle_model = this.integrationsUsingVehicleParamsForResourceRequests.includes(this.currentIntegration) ? this.selectedVehicle?.vehicle_model || this.firstCustomerVehicle?.vehicle_model || "" : "";
		},
		resetIntegrationResourceRequestOptions() {
			this.integrationOptions = {
				integration: this.currentIntegration || "",
				organization_id: this.currentCampaignCustomer.organization_id,
			};

			// reset vehicle options
			this.resetIntegrationResourceRequestVehicleOptions();
		},
		resetIntegrationCalendarResources() {
			this.integrationServiceShops = [];
			this.integrationServiceAdvisors = [];
			this.integrationTransportationTypes = [];
			this.opcodeToServices = {};
			this.serviceToOpcodes = {};
			this.shopMap = new Map();
			this.transportMap = new Map();
		},
		resetAllIntegrationValues() {
			this.resetIntegrationResourceRequestOptions();
			this.resetIntegrationCalendarResources();
			this.integrationCustomer = null;
			this.integrationVehicles = [];
			this.integrationAppointments = [];
			this.integrationCustomerList = [];
			this.displayedIntegrationCustomers = [];
			this.appointmentSlots = [];
			this.integrationAppointmentDetails = null;
			this.scopedSlot = null;
			this.scopedAppointment = null;
			this.showCreateCustomerForm = false;
			this.isIntegrationError = false;
			this.isIntegrationResourceError = false;
			this.integrationAppointmentError = null;
			this.integrationResponse = null;
			this.isExactMatch = false;
			this.customerVehicles = [];
		},
		async resetDisplayedIntegrationCustomers() {
			this.displayedIntegrationCustomers = this.integrationCustomerList.filter(customer => customer.sid !== this.integrationCustomer?.sid);
		},
		resetSlotSelection() {
			this.scopedSlot = null;
			this.slotFormObject = null;
			this.findSlots = true;
		},
		restoreLastIntegrationValues() {
			if (this.currentIntegration === "xtime") {
				if (this.selectedXtimeVehicle) {
					this.integrationOptions.vehicle_year = this.selectedXtimeVehicle?.vehicle_model_year || "";
					this.integrationOptions.vehicle_make = this.selectedXtimeVehicle?.vehicle_make || "";
					this.integrationOptions.vehicle_model = this.selectedXtimeVehicle?.vehicle_model || "";
				}

				if (this.selectedXtimeCustomer) {
					// Load saved customer
					this.integrationCustomer = { ...this.selectedXtimeCustomer };
				} else {
					// No saved customer, fetch customers
					this.getIntegrationCustomers();
					return;
				}

				this.sendIntegrationResourceRequests();
				this.getIntegrationAppointments();
				return;
			}

			if (this.currentIntegration === "tekion") {
				if (this.selectedTekionCustomer) {
					// Load saved customer
					this.integrationCustomer = { ...this.selectedTekionCustomer };
				} else {
					// No saved customer, fetch customers
					this.getIntegrationCustomers();
					return;
				}
				
				this.sendIntegrationResourceRequests();
				this.getIntegrationAppointments();
				return;
			}

			this.isIntegrationResourceError = true;

			return;
		},
		async sendIntegrationResourceRequests() {
			const promises = [];

			if (this.allowedIntegrationsForGetServiceAdvisors.includes(this.currentIntegration)) {
				promises.push(this.getServiceAdvisors());
			}

			if (this.allowedIntegrationsForGetServiceShops.includes(this.currentIntegration)) {
				promises.push(this.getShops());
			}

			if (this.allowedIntegrationsForGetTransportationTypes.includes(this.currentIntegration)) {
				promises.push(this.getTransportationTypes());
			}

			if (this.allowedIntegrationsForGetServiceMenu.includes(this.currentIntegration)) {
				promises.push(this.getServiceMenu());
			}

			// Load all resources in parallel instead of sequentially
			await Promise.allSettled(promises);
		},
		// in case of xtime where we do not have a create customer endpoint, set the integration customer from the form customer
		setIntegrationCustomerFromFormCustomer() {
			if (!this.formCustomer) {
				return;
			}

			// set addresses (same json tags for InAppOutgoingIntegrationCustomerAddresses)
			let addresses = [];
			if (this.formCustomer?.address) {
				let address = {
					line_1: this.formCustomer?.address?.addressLine1 || "",
					line_2: this.formCustomer?.address?.addressLine2 || "",
					city: this.formCustomer?.address?.city || "",
					state: this.formCustomer?.address?.state || "",
					zip: this.formCustomer?.address?.zipCode || "",
					country: this.formCustomer?.address?.country || "",
					is_current: this.formCustomer?.address?.isPrimary || true,
					address_type: this.formCustomer?.address?.addressType || "SHIPPING",
				}

				addresses.push(address);
			}

			// set phones (same json tags for InAppOutgoingIntegrationCustomerPhones)
			let phones = [];
			if (this.formCustomer?.phones?.length > 0) {
				let phone = {
					number: this.formCustomer?.phones?.[0]?.phoneNumber || "",
					phone_type: this.formCustomer?.phones?.[0]?.phoneType || "",
					is_primary: this.formCustomer?.phones?.[0]?.isPrimary || false,
				}

				phones.push(phone);
			}

			let vehicleInfo = null;
			if (this.formCustomer?.vehicleInfo?.year && this.formCustomer?.vehicleInfo?.make && this.formCustomer?.vehicleInfo?.model) {
				vehicleInfo = {
					year: this.formCustomer?.vehicleInfo?.year,
					make: this.formCustomer?.vehicleInfo?.make,
					model: this.formCustomer?.vehicleInfo?.model,
				}
			}

			// build form integration customer object (same json tags for InAppOutgoingIntegrationCustomer)
			let formIntegrationCustomer = {
				company_name: this.formCustomer?.companyName || "",
				email: this.formCustomer?.email || "",
				first_name: this.formCustomer?.firstName || "",
				last_name: this.formCustomer?.lastName || "",
				customer_type: this.formCustomer?.customerType || "",
				addresses: addresses || [],
				phones: phones || [],
				vehicle_info: vehicleInfo || null,
			};

			this.integrationCustomer = { ...formIntegrationCustomer };

			return;
		},
		setIntegrationError(status) {
			if (!status) {
				return;
			}
			if (status >= 400) {
				this.isIntegrationError = true;
			} else {
				this.isIntegrationError = false;
			}
		},
		setScopedAppointment(appointment) {
			if (!appointment) {
				return;
			}

			this.scopedAppointment = appointment;
		},
		setScopedSlot(slot) {
			if (!slot) {
				this.resetSlotSelection();
				return;
			}

			this.scopedSlot = slot;
			this.findSlots = false;
		},
		setSelectedVehicle(vehicle) {
			this.selectedVehicle = vehicle;

			// set integration specific values
			if (this.currentIntegration === "xtime") {
				this.selectedXtimeVehicle = { ...this.selectedVehicle };
			} else if (this.currentIntegration === "tekion") {
				this.selectedTekionVehicle = { ...this.selectedVehicle };
			} else {
				return;
			}
		},
		setSlotFormObject(formObject) {
			if (!formObject) {
				return;
			}

			this.slotFormObject = formObject;
		},
		showCustomerConfirmDialog(customer) {
			if (!customer) {
				return;
			}
			this.selectedCustomerForConfirm = customer;
			this.showConfirmDialog = true;
		},
		showSnackbar(color, text) {
			if (color?.length > 0 && text?.length > 0) {
				this.snackbar.color = color;
				this.snackbar.text = text;
				this.snackbar.show = true;
			}
		},
		titleize(string) {
			return titleize(string);
		},
		updateIntegrationVehicles(newVehicles) {
			// set the integrationVehicles with vehicle_id and customer_vehicle_id from created volie vehicles
			this.integrationVehicles = [...newVehicles];
		},
		filterValidCustomerVehicles(customerVehicles) {
			if (customerVehicles?.length > 0) {
				// The backward iteration prevents the common bug where removing elements while iterating forward causes indices to shift, potentially skipping elements or causing index out - of - bounds errors.
				for (let i = customerVehicles.length - 1; i >= 0; i--) {
					const customerVehicle = customerVehicles[i];
					if (!(customerVehicle?.vin?.length > 0) || !(customerVehicle?.vehicle_model_year?.length > 0) || !(customerVehicle?.vehicle_make?.length > 0) || !(customerVehicle?.vehicle_model?.length > 0)) {
						customerVehicles.splice(i, 1);
					}
				}
			}

			return customerVehicles;
		},
	},
	computed: {
		currentIntegrationHasCreateCustomerEndpoint() {
			return this.currentIntegration === "tekion"; // TODO: add other integrations that have a create customer endpoint as needed
		},
		customerAddresses() {
			return this.customerCommunicationInfo?.customerAddresses || [];
		},
		displayMatchingCustomers() {
			return this.validIntegrationCustomers && (this.switchIntegrationCustomer || (!this.integrationCustomer && !this.showCreateCustomerForm));
		},
		primaryCustomerAddress() {
			return this.customerAddresses?.find(address => address.isPrimary) || null;
		},
		displayAppointmentAlert() {
			return this.isDetailsState && this.validIntegrationCustomer && (this.integrationAppointmentError || this.integrationAppointments?.length === 0) && !this.isIntegrationResourceError;
		},
		displayLoadingCircle() {
			return !this.isCommunicationManagerSet || this.isIntegrationCustomerLoading || this.isLoading || this.isIntegrationResourceLoading || this.isConfirmingCustomer;
		},
		eligibleToSaveCustomer() {
			return this.formCustomer?.firstName
				&& this.formCustomer?.lastName
				&& this.formCustomer?.email
				&& this.formCustomer?.phones?.[0]?.phoneNumber
				&& this.formCustomer?.phones?.[0]?.phoneType
				&& ((this.formCustomer?.customerType === 'BUSINESS' && this.formCustomer?.companyName?.length > 0)
					|| (this.formCustomer?.customerType === 'INDIVIDUAL' && (!(this.formCustomer?.companyName?.length > 0)))
				)
			;
		},
		firstCustomerVehicle() {
			return this.customerVehicles?.[0] || null;
		},
		hasPreviouslySetIntegrationCustomer() {
			return this.validSelectedTekionCustomer || this.validSelectedXtimeCustomer; // Plus others as needed
		},
		isCreateState() {
			return this.currentView === 'create' && this.scopedSlot;
		},
		isDetailsState() {
			return this.currentView === 'details' && !this.scopedSlot;
		},
		isIntegrationResourceLoading() {
			return this.isIntegrationCustomerLoading || this.isIntegrationAppointmentsLoading || this.isServiceMenuLoading || this.isIntegrationServiceShopsLoading || this.isIntegrationTransportationTypesLoading || this.isIntegrationServiceAdvisorsLoading;
		},
		isRescheduleState() {
			return this.currentView === 'reschedule' && this.scopedAppointment;
		},
		isSlotsState() {
			return this.currentView === 'slots-selector' && !this.scopedSlot;
		},
		itemColor() {
			return resourceColorClass("Service");
		},
		showAppointmentCreateButton() {
			return !this.showCreateCustomerForm && this.validIntegrationCustomer && this.isDetailsState && !this.switchIntegrationCustomer;
		},
		showCustomerOptions() {
			return !this.integrationCustomer && !this.showCreateCustomerForm && !this.isIntegrationError;
		},
		showCustomerSelectionList() {
			return !this.showCreateCustomerForm && this.displayedIntegrationCustomers?.length > 0 && (this.switchIntegrationCustomer || (!this.isIntegrationError && !this.isIntegrationResourceError && !this.validIntegrationCustomer && !this.showCreateCustomerForm));
		},
		showNoCustomersFoundError() {
			return (!(this.integrationCustomer) && !(this.validIntegrationCustomers) && !(this.isIntegrationError) && !this.isIntegrationResourceError);
		},
		validCurrentCampaignCustomer() {
			return this.currentCampaignCustomer?.customer_id > 0;
		},
		validCustomerVehicles() {
			return this.customerVehicles?.length > 0;
		},
		validIntegrationAppointments() {
			return this.integrationAppointments?.length > 0;
		},
		validIntegrationCustomer() {
			if (!this.integrationCustomer) {
				return false;
			}

			switch (this.currentIntegration) {
				case 'tekion':
					return this.integrationCustomerMatchesSelectedTekionCustomer;
				case 'xtime':
					return this.integrationCustomerMatchesSelectedXtimeCustomer;
				default:
					return false;
			}
		},
		validIntegrationCustomers() {
			return this.integrationCustomerList?.length > 0;
		},
		validIntegrationResponse() {
			return this.integrationResponse?.length > 0;
		},
		integrationCustomerMatchesSelectedTekionCustomer() {
			return (this.validSelectedTekionCustomer || this.associatedTekionCustomer) && this.integrationCustomer?.sid === this.selectedTekionCustomer?.sid;
		},
		validSelectedTekionCustomer() {
			return this.selectedTekionCustomer?.sid?.length > 0;
		},
		validSelectedXtimeCustomer() {
			return this.selectedXtimeCustomer !== undefined && this.selectedXtimeCustomer !== null;
		},
		integrationCustomerMatchesSelectedXtimeCustomer() {
			return this.validSelectedXtimeCustomer && this.selectedXtimeCustomer?.first_name === this.integrationCustomer?.first_name && this.selectedXtimeCustomer?.last_name === this.integrationCustomer?.last_name;
		},
		showSwitchIntegrationCustomerButton() {
			return this.currentIntegration === 'tekion' && (!this.showCreateCustomerForm || this.switchIntegrationCustomer) && this.validSelectedTekionCustomer;
		},
	},
	components: {
		Create,
		CustomerForm,
		Details,
		Slots,
		Reschedule,
		ExpandableCardListItem,
		ListItem,
		StrLimitTooltipText,
		ShowMoreRow,
	},
	watch: {
		source(newVal) {
			if (newVal?.length > 0) {
				this.reInitIntegrationValues();
				this.displayDetailsView();
			}
		},
		customerCommunicationInfo: {
			handler(newVal) {
				if (newVal) {
					this.initFormCustomer();
				}
			},
			immediate: true,
			deep: true
		},
		toggleRunGetIntegrationsCustomers(newVal, oldVal) {
			if (newVal !== oldVal) {
				this.integrationCustomer = null;
				this.getIntegrationCustomers();
			}
		},
	},
	props: {
		currentCampaignCustomer: {
			type: Object,
			required: true,
		},
		currentIntegration: String,
		customerCommunicationInfo: Object,
		inCommunicationCenter: Boolean,
		isCommunicationManagerSet: Boolean,
		setIntegrationAppointmentDetails: Function,
		setIntegrationCustomerSid: Function,
		setAppointmentIdForIntegration: Function,
		source: String,
		toggleRunGetIntegrationsCustomers: Boolean,
	},
	created() {
		this.reInitIntegrationValues();
	},
	destroyed() {
	}
});
</script>

<style lang="scss" scoped>
.customer-card {
	&:hover {
		.customer-overlay {
			opacity: 1 !important;
			visibility: visible !important;
		}
		transform: translateY(-2px);
		box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
		background-color: rgba(25, 118, 210, 0.05);
	}
}

.customer-overlay {
	opacity: 0;
	visibility: hidden;
	transition: all 0.2s ease;
	pointer-events: none;
	
	&:hover {
		pointer-events: all;
	}
}
</style>
